/**
 * The one interface every console data read goes through (ORB-756).
 *
 * A mock satisfies it today and the query tier satisfies it later. The People Data Labs files land
 * the week of 8 September and the schema, the primary key and the real fill rates are all
 * unconfirmed, so everything that depends on them sits behind this and the UI is written once.
 */

import type { ConsoleObject, FieldDef } from './fieldCatalogue'

/**
 * A count, or a floor. The SQL store counts every filter exactly (ORB-1016); `atLeast` is how the mock
 * reports the population it stands in for, and the union keeps every caller able to render "10,000+".
 */
export type Total = { exact: number } | { atLeast: number }

/** Opaque page cursor. The store's is a keyset position, the mock's an offset. Never parsed by the UI. */
export type Cursor = string | null

/** The counts both lenses show. A null count was not taken. */
export interface ProspectTotals {
  people: Total | null
  /** What the Companies lens lists. */
  companies: Total | null
  /** Distinct employers of the matching people: the "M" in "N people at M companies". */
  employers: Total | null
}

export interface PageResult<T> {
  rows: T[]
  /** null when this is the last page. */
  nextCursor: Cursor
  /** On the first page only. */
  totals: ProspectTotals | null
  /** Companies lens only: people matching the filter at each company on this page, by company id. */
  matchingPeople: Record<string, number> | null
}

export type FilterOperator =
  | 'contains'
  | 'not_contains'
  | 'equals'
  | 'not_equals'
  | 'is_any_of'
  | 'is_none_of'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'between'
  | 'within_last_days'
  | 'is_empty'
  | 'is_not_empty'

export type FilterValue = string | number | boolean | Array<string | number> | null

/**
 * How the Prospects surface reads one filter (ORB-815): as the people it finds, or as the companies
 * they work at. A lens is a URL segment's word, an object is a catalogue's, and they are spelled
 * differently on purpose.
 */
export type Lens = 'people' | 'companies'

export const LENSES: readonly Lens[] = ['people', 'companies']

export const LENS_OBJECT: Record<Lens, ConsoleObject> = { people: 'person', companies: 'company' }

/** One leaf clause. `field` is a key of `object`'s catalogue — nothing else is addressable. */
export interface FilterClause {
  kind: 'clause'
  /**
   * Required, never inferred (ORB-815). The two catalogues share seventeen keys, so `location_country`
   * alone is a person's country on one object and a company's on the other.
   */
  object: ConsoleObject
  field: string
  operator: FilterOperator
  value: FilterValue
  /**
   * True when a model wrote this clause rather than a person. Drives the magenta dot on the chip;
   * editing a clause by hand clears it, because it is yours now.
   */
  authoredByAgent?: boolean
}

export interface FilterGroup {
  kind: 'group'
  combinator: 'and' | 'or'
  negate?: boolean
  children: FilterNode[]
}

export type FilterNode = FilterClause | FilterGroup

/** An empty `and` group. The compiler treats it as "everything". */
export type FilterAst = FilterGroup

export const EMPTY_FILTER: FilterAst = { kind: 'group', combinator: 'and', children: [] }

/**
 * One person as the grid reads them. Keys are fieldCatalogue keys; a missing key is a genuinely
 * empty cell and must render as one. Values are already display-shaped — the store does the
 * derivation, because sorting hundreds of millions of rows on a computed expression is not a client
 * concern.
 */
export interface PersonRow {
  id: string
  [field: string]: string | number | boolean | null | undefined
}

/**
 * One company as the grid reads them, on exactly the same terms as a person (ORB-762).
 *
 * Deliberately not a nested shape. `location: { country, region }` and `employee_growth_rate:
 * { '12_month': n }` are how PDL delivers a company, and neither is addressable by a filter clause,
 * sortable by the store, or renderable as a cell without the client learning the vendor's document
 * layout. The store flattens and derives at ingest; the client renders keys.
 */
export interface CompanyRow {
  id: string
  [field: string]: string | number | boolean | null | undefined
}

/** A row of whichever object the grid is pointed at. Both are a flat bag of catalogue keys. */
export type ObjectRow = PersonRow | CompanyRow

/** Which of an open workspace list's people a read returns (ORB-806). */
export type ListView = 'all' | 'added' | 'removed'

/** A read through a workspace list rather than over the whole index. The mock ignores it. */
export interface ListScope {
  id: string
  view: ListView
}

/**
 * A read through one segment of a campaign list (ORB-1043). The campaign is named because the API
 * matches the campaign list to the campaign in the route. The mock ignores it.
 */
export interface SegmentScope {
  campaignId: string
  campaignListId: string
  segmentId: string
}

export interface ListOptions {
  pageSize?: number
  sort?: { field: string; direction: 'asc' | 'desc' }
  signal?: AbortSignal
  list?: ListScope
  segment?: SegmentScope
}

/**
 * The row key a read through a list carries for someone edited by hand: `include` or `exclude`
 * (ORB-806). Folded into the row by the adapter, so a cell reads it like any other key.
 */
export const LIST_EDIT_KEY = 'list_edit'

/**
 * A funding round as PDL delivers one. Sidecar only — `funding_details` is 0.3% populated across the
 * company file, so it is never a column.
 */
export interface FundingRound {
  funding_round_id: string
  funding_type: string
  funding_currency: string
  funding_raised: number | null
  funding_date: string
  investors: string[]
}

/**
 * The half of a company record that must never travel in a list query (ORB-762).
 *
 * `employee_count_by_month_by_role` and `employee_count_by_month_by_level` alone are about 55% of a
 * 125KB PDL company record, and they are chart fodder: a monthly series keyed by role is not a cell
 * at any density. Paying for them on every row of every page in order to render none of them is the
 * cost this split exists to avoid — 100 rows a page against a 125KB record is 12.5MB per page, of
 * which roughly 7MB is a series the grid cannot draw.
 *
 * Modelled against the mock now because retrofitting it after the real ingest is not free: a list
 * endpoint that has ever returned these has consumers that read them, and the split then has to be
 * negotiated rather than declared.
 *
 * Keys are PDL's own, in PDL's snake_case, for the same reason a row's keys are: the correspondence
 * to the vendor's schema has to be checkable by eye when the sample file lands.
 */
export interface CompanySidecar {
  id: string
  /** Role → ISO month (`YYYY-MM`) → headcount. */
  employee_count_by_month_by_role: Record<string, Record<string, number>>
  /** Seniority level → ISO month (`YYYY-MM`) → headcount. */
  employee_count_by_month_by_level: Record<string, Record<string, number>>
  /** Company name → number of leavers who went there. */
  top_next_employers: Record<string, number>
  /** Company name → number of joiners who came from there. */
  top_previous_employers: Record<string, number>
  funding_details: FundingRound[]
}

/** One role in a person's history, as PDL delivers it. Sidecar only — see `PersonDetail`. */
export interface PersonRole {
  company?: { name?: string | null; size?: string | null; industry_v2?: string | null } | null
  title?: { name?: string | null } | null
  start_date?: string | null
  end_date?: string | null
  is_primary?: boolean | null
}

/** One school, on the same terms. */
export interface PersonSchooling {
  school?: { name?: string | null; type?: string | null } | null
  degrees?: string[] | null
  majors?: string[] | null
  start_date?: string | null
  end_date?: string | null
}

/**
 * The half of a person record that must never travel in a list query (ORB-766).
 *
 * `experience` is 55% of a 12KB PDL person record and `education` another 11%, so three quarters of
 * every person is history that no column renders and no filter clause addresses. A page of 100 rows
 * carrying it is 1.2MB to draw 280KB. The thin row holds a one-line summary of each — "Red Points,
 * Kantox +2" — and this is what is behind that line.
 *
 * The person half of the same split `CompanySidecar` describes, and it arrived later for a reason
 * worth recording: until the real file landed, `experience` was a `fallbackValue` stub roughly
 * twenty bytes long, so there was nothing for a peek panel to show and no cost to carrying it.
 *
 * Keys are PDL's own, in PDL's snake_case, so the correspondence to the vendor's schema stays
 * checkable by eye.
 */
export interface PersonDetail {
  id: string
  experience: PersonRole[]
  education: PersonSchooling[]
  skills: string[]
}

export interface ConsoleAdapter {
  /**
   * Whether the rows behind this adapter were generated rather than delivered.
   *
   * ⚑ Here because a record panel asserts it in words — "no real person is behind it" — and that
   * sentence became a lie the moment the console was pointed at the real store (ORB-766). A panel
   * cannot tell which adapter it is rendering, and a claim about the provenance of the data is
   * exactly the thing that must not be guessed on a surface whose whole purpose is provenance.
   */
  readonly synthetic: boolean
  listPeople(q: FilterAst, page: Cursor, options?: ListOptions): Promise<PageResult<PersonRow>>
  listCompanies(q: FilterAst, page: Cursor, options?: ListOptions): Promise<PageResult<CompanyRow>>
  /**
   * The catalogue for one object.
   *
   * A parameter rather than a second method, so a third object costs an entry in a record and
   * nothing else. Required rather than defaulting to `person`: a default would let a caller that
   * forgot to say which object it is reading return the wrong catalogue and be believed.
   */
  getFieldCatalogue(object: ConsoleObject): Promise<FieldDef[]>
  /** The record panel's second read. Never part of a list payload — see `CompanySidecar`. */
  getCompanySidecar(id: string, options?: { signal?: AbortSignal }): Promise<CompanySidecar>
  /** The same read for a person. Never part of a list payload — see `PersonDetail`. */
  getPersonDetail(id: string, options?: { signal?: AbortSignal }): Promise<PersonDetail>
}

/** Renders a Total for a human. `atLeast` is the honest form when the store cannot count. */
export function formatTotal(total: Total): string {
  return 'exact' in total
    ? total.exact.toLocaleString('en-GB')
    : `${total.atLeast.toLocaleString('en-GB')}+`
}
